
import { useEffect, useMemo } from 'react'
import { Grid } from '../Grid/Grid.js'
import { ScaledGrid } from '../Grid/ScaledGrid.js'
import { useGameStore } from '../../store/gameStore.js'
import { useStatsStore } from '../../store/statsStore.js'
import { useAudio } from '../../hooks/useAudio.js'
import { logo } from '../../assets/index.js'
import type { CellState, CellResult } from '@fleetduel/shared'
import { coordKey, getShipCells, Ship } from '@fleetduel/shared'

interface ResultsProps {
  onReplay: () => void
}

function buildBoardStates(ships: Ship[], hits: CellResult[]): Map<string, CellState> {
  const states = new Map<string, CellState>()
  const hitKeys = new Set(hits.filter(h => h.hit).map(h => coordKey(h.coord)))
  const missKeys = new Set(hits.filter(h => !h.hit).map(h => coordKey(h.coord)))

  for (const ship of ships) {
    const cells = getShipCells(ship)
    const isSunk = cells.every(c => hitKeys.has(coordKey(c)))

    for (const cell of cells) {
      const key = coordKey(cell)
      if (isSunk) {
        states.set(key, 'sunk')
      } else if (hitKeys.has(key)) {
        states.set(key, 'hit')
      }
    }
  }

  for (const key of missKeys) {
    if (!states.has(key)) {
      states.set(key, 'miss')
    }
  }

  return states
}

const statRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '4px 0',
  fontSize: 14,
  opacity: 0,
  animation: 'stagger-fade-in 300ms ease-out forwards',
}

export function Results({ onReplay }: ResultsProps) {
  const { playerIndex, winner, revealedBoards, revealedHits } = useGameStore()
  const stats = useStatsStore()
  const { play } = useAudio()
  const isWinner = winner === playerIndex
  const opponentIndex: 0 | 1 = playerIndex === 0 ? 1 : 0

  // Play victory/defeat sound once
  useEffect(() => {
    play(isWinner ? 'victory' : 'defeat', 'combat')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const accuracy = stats.matchShots > 0
    ? Math.round((stats.matchHits / stats.matchShots) * 100)
    : 0

  const myBoardStates = useMemo(() => {
    if (!revealedBoards || !revealedHits) return new Map<string, CellState>()
    return buildBoardStates(revealedBoards[playerIndex!], revealedHits[playerIndex!])
  }, [revealedBoards, revealedHits, playerIndex])

  const enemyBoardStates = useMemo(() => {
    if (!revealedBoards || !revealedHits) return new Map<string, CellState>()
    return buildBoardStates(revealedBoards[opponentIndex], revealedHits[opponentIndex])
  }, [revealedBoards, revealedHits, opponentIndex])

  const statRows = [
    { label: 'Total turns', value: stats.matchTurns },
    { label: 'Accuracy', value: `${accuracy}%` },
    { label: 'Torpedoes used', value: `${stats.matchTorpedoesUsed}/2` },
    { label: 'Sonar pings used', value: `${stats.matchSonarUsed}/2` },
    { label: 'Ships lost', value: stats.matchShipsLost },
    { label: 'First blood', value: stats.matchFirstBlood === 'player' ? 'You' : stats.matchFirstBlood === 'opponent' ? 'Opponent' : '—' },
  ]

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 100,
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 16,
      padding: 16,
      // Translucent scrim so the battlefield stays visible behind the results
      backgroundColor: 'rgba(11, 37, 69, 0.78)',
      backdropFilter: 'blur(3px)',
      WebkitBackdropFilter: 'blur(3px)',
    }}>
      <img src={logo} alt="Salvo" style={{ width: 'min(180px, 36vw)' }} draggable={false} />

      {/* Result banner — ART_GUIDE_v2 §4 */}
      <div style={{
        fontSize: 'clamp(28px, 8vw, 40px)',
        fontWeight: 800,
        letterSpacing: 4,
        color: isWinner ? '#F4B942' : '#8DA9C4',
        textAlign: 'center',
        opacity: 0,
        animation: 'stagger-fade-in 400ms cubic-bezier(0.22,1,0.36,1) forwards',
      }}>
        {isWinner ? 'VICTORY' : 'DEFEAT'}
      </div>

      {/* Stats panel — ART_GUIDE_v2 §4 */}
      <div style={{
        backgroundColor: '#13315C',
        border: '2px solid #F4B942',
        borderRadius: 8,
        padding: '16px 24px',
        width: '100%',
        maxWidth: 360,
      }}>
        {statRows.map((row, i) => (
          <div
            key={row.label}
            style={{
              ...statRowStyle,
              animationDelay: `${(i + 1) * 60}ms`,
            }}
          >
            <span style={{ color: '#8DA9C4' }}>{row.label}</span>
            <span style={{ color: '#EAF2F8', fontWeight: 600 }}>{row.value}</span>
          </div>
        ))}
      </div>

      {/* Board reveals — scaled down so both fit side by side on phones */}
      {revealedBoards && (
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: 16,
          justifyContent: 'center',
        }}>
          <div style={{ width: 'min(44vw, 280px)' }}>
            <div style={{ fontSize: 12, color: '#8DA9C4', marginBottom: 4, textAlign: 'center' }}>
              Enemy Fleet
            </div>
            <ScaledGrid>
              <Grid
                cellStates={enemyBoardStates}
                isOwnBoard={true}
                ships={revealedBoards[opponentIndex]}
              />
            </ScaledGrid>
          </div>

          <div style={{ width: 'min(44vw, 280px)' }}>
            <div style={{ fontSize: 12, color: '#8DA9C4', marginBottom: 4, textAlign: 'center' }}>
              Your Fleet
            </div>
            <ScaledGrid>
              <Grid
                cellStates={myBoardStates}
                isOwnBoard={true}
                ships={revealedBoards[playerIndex!]}
              />
            </ScaledGrid>
          </div>
        </div>
      )}

      {/* Buttons — ART_GUIDE_v2 §4 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', maxWidth: 360 }}>
        <button
          onClick={onReplay}
          style={{
            padding: '14px 24px',
            fontSize: 16,
            fontWeight: 700,
            border: 'none',
            borderRadius: 8,
            backgroundColor: '#F4B942',
            color: '#0B2545',
            cursor: 'pointer',
            height: 44,
          }}
        >
          Play Again
        </button>
        <button
          onClick={onReplay}
          style={{
            padding: '14px 24px',
            fontSize: 16,
            fontWeight: 700,
            border: '2px solid #8DA9C4',
            borderRadius: 8,
            backgroundColor: '#13315C',
            color: '#EAF2F8',
            cursor: 'pointer',
            height: 44,
          }}
        >
          Main Menu
        </button>
      </div>
    </div>
  )
}
